import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import groupBy from "lodash/groupBy";
import isString from "lodash/isString";
import isNil from "lodash/isNil";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import CommentItem from "./CommentItem";
import { useMemo, useState } from "react";
import format from "date-fns/format";
import isValid from "date-fns/isValid";
import Divider from "@mui/material/Divider";
import { useTheme } from "@mui/material/styles";
import SelectInput from "../../components/inputs/SelectInput";
import SortOutlined from "@mui/icons-material/SortOutlined";

const CommentItemRenderer = ({ comments, sx, px, gap = 3, padding = 2 }) => {
  const [sort, setSort] = useState("desc");
  console.log("comments", comments);
  const groupedComments = useMemo(() => {
    if (!isArray(comments) || isEmpty(comments)) {
      return {};
    }

    // Group comments by ISO date key "yyyy-MM-dd"
    return groupBy(comments, (comment) => {
      const dateStr = comment?.commentDateTime;
      if (!dateStr) return "Unknown Date";

      const date = isoToDate(dateStr);
      if (!isValid(date)) return "Unknown Date";

      // Use a stable key for grouping and sorting
      return format(date, "yyyy-MM-dd");
    });
  }, [comments]);

  // Store the actual date objects for display to maintain timezone consistency
  const dateKeyToDateMap = useMemo(() => {
    const map = {};

    Object.keys(groupedComments).forEach((dateKey) => {
      if (dateKey === "Unknown Date") {
        map[dateKey] = null;
      } else {
        // Get the first comment from this group to extract the original date
        const firstComment = groupedComments[dateKey][0];
        const originalDate = isoToDate(firstComment?.commentDateTime);
        map[dateKey] = originalDate;
      }
    });

    return map;
  }, [groupedComments]);

  const sortedDateKeys = useMemo(() => {
    return Object.keys(groupedComments).sort((a, b) => {
      if (a === "Unknown Date") return 1;
      if (b === "Unknown Date") return -1;

      // Since keys are "yyyy-MM-dd", lexicographic compare works for date order.
      if (sort === "desc") {
        // Most recent first (newest to oldest)
        return b.localeCompare(a);
      } else {
        // Least recent first (oldest to newest)
        return a.localeCompare(b);
      }
    });
  }, [groupedComments, sort]);

  // Sort comments within each date group
  const sortedGroupedComments = useMemo(() => {
    const sorted = {};

    Object.keys(groupedComments).forEach((dateKey) => {
      const commentsInGroup = groupedComments[dateKey];

      // Sort comments within the group by their actual datetime
      sorted[dateKey] = [...commentsInGroup].sort((a, b) => {
        const dateA = isoToDate(a?.commentDateTime);
        const dateB = isoToDate(b?.commentDateTime);

        // Handle invalid dates
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        if (sort === "desc") {
          // Most recent first (newest to oldest)
          return dateB.getTime() - dateA.getTime();
        } else {
          // Least recent first (oldest to newest)
          return dateA.getTime() - dateB.getTime();
        }
      });
    });

    return sorted;
  }, [groupedComments, sort]);

  if (!isArray(comments) || isEmpty(comments)) {
    return (
      <RootContainer gap={gap} sx={sx} padding={padding}>
        <Txt>No comments added</Txt>
      </RootContainer>
    );
  }

  return (
    <RootContainer gap={gap} sx={sx} padding={padding}>
      <Flx
        // end
        ac
        fw
        gap={1}
        sx={{
          opacity: 0.65,
          fieldset: {
            border: "none",
          },
          ".MuiSelect-select": {
            padding: "0px",
            fontSize: "12px",
          },
        }}
      >
        <SortOutlined className="thin" sx={{ fontSize: "18px" }} />

        <SelectInput
          value={sort}
          fullWidth={false}
          sx={{ width: "105px" }}
          onChange={setSort}
          options={[
            { label: "Most Recent", value: "desc" },
            { label: "Least Recent", value: "asc" },
          ]}
        />
      </Flx>

      {sortedDateKeys.map((dateKey) => {
        const originalDate = dateKeyToDateMap[dateKey];
        const displayDate =
          dateKey === "Unknown Date" || !originalDate
            ? "Unknown Date"
            : format(originalDate, "EEEE, M/d/yyyy"); // Use original date object

        return (
          <DateGroup key={dateKey}>
            {/* Header now includes weekday */}
            <CreatedDate>{displayDate}</CreatedDate>

            {/* Render comments for this date */}
            <Flx column gap={2}>
              {sortedGroupedComments[dateKey].map((c) => (
                <CommentItem
                  key={c?._id}
                  px={px}
                  createdDate={c?.commentDateTime}
                  createdBy={c?.postedBy}
                  htmlString={c?.htmlString}
                  data={c?.data}
                />
              ))}
            </Flx>
          </DateGroup>
        );
      })}
    </RootContainer>
  );
};

const CreatedDate = ({ children }) => {
  const theme = useTheme();
  if (isNil(children)) {
    return null;
  }
  return (
    <Divider sx={{ mb: 2, opacity: 0.6 }}>
      <Txt
        center
        body2
        sx={{
          fontWeight: 400,
          color: theme.palette.text.secondary,
          fontSize: "11px",
          lineHeight: 2,
        }}
      >
        {children}
      </Txt>
    </Divider>
  );
};

const DateGroup = ({ children }) => {
  return (
    <Flx column gap={1} fw sx={{ mb: 2 }}>
      {children}
    </Flx>
  );
};

const RootContainer = ({ children, padding, sx }) => {
  const styles = useMemo(() => {
    return { p: padding, ...sx };
  }, [padding, sx]);
  return (
    <Flx column fw gap={3} sx={styles}>
      {children}
    </Flx>
  );
};

// Helper function from your CommentItem component
const isoToDate = (isoString) => {
  // Handle empty or non-string values
  if (!isoString || !isString(isoString)) {
    return null;
  }

  // Try to create a date object
  const date = new Date(isoString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    console.warn("Invalid ISO string format:", isoString);
    return null;
  }

  return date;
};

export default CommentItemRenderer;
